import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, CheckCircle2, XCircle, Filter, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { value: 'revenue', label: 'Ricavi', color: 'bg-green-100 text-green-800' },
  { value: 'variable_cost', label: 'Costi Variabili', color: 'bg-orange-100 text-orange-800' },
  { value: 'fixed_cost', label: 'Costi Fissi', color: 'bg-red-100 text-red-800' },
  { value: 'depreciation', label: 'Ammortamenti', color: 'bg-purple-100 text-purple-800' },
  { value: 'financial', label: 'Oneri Finanziari', color: 'bg-blue-100 text-blue-800' },
  { value: 'tax', label: 'Imposte', color: 'bg-slate-100 text-slate-800' },
  { value: 'admin_compensation', label: 'Compenso Amministr.', color: 'bg-amber-100 text-amber-800' },
  { value: 'brand_royalties', label: 'Royalties Marchio', color: 'bg-pink-100 text-pink-800' },
  { value: 'rimanenze_iniziali', label: 'Rimanenze Iniziali', color: 'bg-teal-100 text-teal-800' },
  { value: 'rimanenze_finali', label: 'Rimanenze Finali', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'sp', label: 'Patrimoniale (SP)', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'exclude', label: 'Escludi', color: 'bg-gray-100 text-gray-500' },
];

const formatCurrency = (val) => {
  if (val === null || val === undefined) return '—';
  return val.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

export default function AccountConfigPage({ previewData, clientId, onSave, onCancel }) {
  const entries = previewData?.entries || [];

  // Initialize account selections: include suggested, exclude others
  const [selections, setSelections] = useState(() => {
    const initial = {};
    entries.forEach(e => {
      const included = e.suggested === 'include';
      initial[e.account_code] = {
        included,
        category: included ? guessCategory(e) : 'exclude',
      };
    });
    return initial;
  });

  const [filter, setFilter] = useState('all'); // all, included, excluded, parents, patrimonial
  const [saving, setSaving] = useState(false);

  const toggleAccount = useCallback((code) => {
    setSelections(prev => {
      const current = prev[code];
      const newIncluded = !current.included;
      return {
        ...prev,
        [code]: {
          included: newIncluded,
          category: newIncluded ? guessCategory(entries.find(e => e.account_code === code)) : 'exclude',
        }
      };
    });
  }, [entries]);

  const setCategory = useCallback((code, category) => {
    setSelections(prev => ({
      ...prev,
      [code]: { ...prev[code], category }
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the mappings to save
      const mappings = entries.map(e => ({
        account_code: e.account_code,
        account_name: e.account_name,
        category: selections[e.account_code]?.included
          ? selections[e.account_code].category
          : 'exclude',
      }));

      await axios.post(`${API}/clients/${clientId}/mappings`, { mappings });
      toast.success('Configurazione conti salvata con successo');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Filter entries
  const filteredEntries = entries.filter(e => {
    if (filter === 'included') return selections[e.account_code]?.included;
    if (filter === 'excluded') return !selections[e.account_code]?.included;
    if (filter === 'parents') return e.is_parent;
    if (filter === 'patrimonial') return e.is_patrimonial;
    if (filter === 'ce') return e.is_ce && !e.is_parent;
    return true;
  });

  const includedCount = Object.values(selections).filter(s => s.included).length;
  const excludedCount = Object.values(selections).filter(s => !s.included).length;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="account-config-page">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Configurazione Conti</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Da includere</p>
            <p className="text-2xl font-bold text-green-700" data-testid="config-included-count">{includedCount}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Da escludere</p>
            <p className="text-2xl font-bold text-red-700" data-testid="config-excluded-count">{excludedCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Conti padre (somma)</p>
            <p className="text-2xl font-bold">{previewData?.parent_entries || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Conti patrimoniali</p>
            <p className="text-2xl font-bold">{previewData?.patrimonial_entries || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Configura ogni conto:</strong> Classifica ciascuna voce del bilancio di verifica.
            Le voci patrimoniali (SP) vanno marcate come "Patrimoniale (SP)" o escluse.
            Le voci di Conto Economico vanno classificate per categoria.
            Questa configurazione viene salvata e applicata automaticamente a tutti gli import successivi.
          </p>
        </CardContent>
      </Card>

      {/* Live Totals */}
      <Card className="mb-6 border-emerald-200 bg-emerald-50/50">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground mb-2 font-semibold">Riepilogo classificazione corrente</p>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Ricavi:</span>
              <span className="font-bold ml-1 text-green-700">
                {formatCurrency(entries.filter(e => selections[e.account_code]?.category === 'revenue').reduce((s, e) => s + (e.amount || 0), 0))}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Costi Var.:</span>
              <span className="font-bold ml-1 text-orange-700">
                {formatCurrency(entries.filter(e => selections[e.account_code]?.category === 'variable_cost').reduce((s, e) => s + (e.amount || 0), 0))}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Costi Fissi:</span>
              <span className="font-bold ml-1 text-red-700">
                {formatCurrency(entries.filter(e => selections[e.account_code]?.category === 'fixed_cost').reduce((s, e) => s + (e.amount || 0), 0))}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Risultato stimato:</span>
              <span className="font-bold ml-1">
                {(() => {
                  let rev=0, vc=0, fc=0, dep=0, fin=0, tax=0;
                  entries.forEach(e => {
                    const cat = selections[e.account_code]?.category;
                    const amt = e.amount || 0;
                    if (cat === 'revenue' || cat === 'rimanenze_finali') rev += amt;
                    else if (cat === 'variable_cost' || cat === 'rimanenze_iniziali') vc += amt;
                    else if (cat === 'fixed_cost' || cat === 'admin_compensation' || cat === 'brand_royalties') fc += amt;
                    else if (cat === 'depreciation') dep += amt;
                    else if (cat === 'financial') fin += amt;
                    else if (cat === 'tax') tax += amt;
                  });
                  return formatCurrency(rev - vc - fc - dep - fin - tax);
                })()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter & Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>
            Tutti ({entries.length})
          </Button>
          <Button variant={filter === 'ce' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('ce')}>
            <CheckCircle2 className="h-3 w-3 mr-1" /> CE Foglia ({previewData?.ce_entries || 0})
          </Button>
          <Button variant={filter === 'parents' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('parents')}>
            Padri ({previewData?.parent_entries || 0})
          </Button>
          <Button variant={filter === 'patrimonial' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('patrimonial')}>
            Patrimoniali ({previewData?.patrimonial_entries || 0})
          </Button>
          <Button variant={filter === 'included' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('included')}>
            Inclusi ({includedCount})
          </Button>
          <Button variant={filter === 'excluded' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('excluded')}>
            Esclusi ({excludedCount})
          </Button>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Configurazione'}
        </Button>
      </div>

      {/* Account Table */}
      <Card>
        <CardContent className="p-0">
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="p-3 text-left w-12">Incl.</th>
                  <th className="p-3 text-left">Codice</th>
                  <th className="p-3 text-left">Descrizione</th>
                  <th className="p-3 text-right">Importo</th>
                  <th className="p-3 text-left">Tipo</th>
                  <th className="p-3 text-left w-48">Categoria</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((e, idx) => {
                  const sel = selections[e.account_code] || { included: false, category: 'exclude' };
                  const isIncluded = sel.included;
                  return (
                    <tr
                      key={e.account_code + idx}
                      className={`border-t transition-colors ${isIncluded ? 'bg-white' : 'bg-slate-50/80 text-muted-foreground'} ${e.is_parent ? 'italic' : ''}`}
                      data-testid={`config-row-${e.account_code}`}
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={isIncluded}
                          onCheckedChange={() => toggleAccount(e.account_code)}
                          data-testid={`config-check-${e.account_code}`}
                        />
                      </td>
                      <td className="p-3 font-mono text-xs">{e.account_code}</td>
                      <td className="p-3">{e.account_name}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(e.amount)}</td>
                      <td className="p-3">
                        {e.is_parent && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Padre</Badge>}
                        {e.is_patrimonial && !e.is_parent && <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500">Patrimoniale</Badge>}
                        {e.is_ce && !e.is_parent && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">CE</Badge>}
                        {e.section && <span className="text-xs text-muted-foreground ml-1">{e.section}</span>}
                      </td>
                      <td className="p-3">
                        {isIncluded ? (
                          <Select value={sel.category} onValueChange={(v) => setCategory(e.account_code, v)}>
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.filter(c => c.value !== 'exclude').map(c => (
                                <SelectItem key={c.value} value={c.value}>
                                  <span className={`px-2 py-0.5 rounded text-xs ${c.color}`}>{c.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground">Escluso</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save Bar */}
      <div className="mt-4 flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {includedCount} conti selezionati su {entries.length} totali
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn-bottom">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva e Procedi con Import'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function guessCategory(entry) {
  if (!entry) return 'fixed_cost';
  const section = entry.section || '';
  if (section.includes('Ricavi')) return 'revenue';
  if (section.includes('variabili') || section.includes('produzione') || section.includes('servizi')) return 'variable_cost';
  if (section.includes('fissi') || section.includes('Costi')) return 'fixed_cost';
  if (section.includes('Ammort')) return 'depreciation';
  if (section.includes('finanziari')) return 'financial';
  if (section.includes('Impost') || section.includes('straordinari')) return 'tax';
  return 'fixed_cost';
}
